// A sheet's properties panel and its viewports': the paper, the title block,
// the display toggles, the disciplines shown, the revisions table; and where a
// sheet's schedule tables sit on the paper. docs/claude/sheets.md.
import {
  type Revision,
  type Sheet,
} from '../../shared/types';
import { undoableLiveEdit } from './connectionUi';
import {
  invalidate,
  layers,
  newId,
  pushHistory,
  redrawBackdrop,
  renderProperties,
  state,
} from './core';
import { tr } from '../i18n';
import { layerObjectIds } from '../layers';
import { curLang } from './nameFields';
import {
  buttonRow,
  deleteRow,
  infoRow,
  labeledField,
  numberRow,
  sectionTitle,
} from './panelRows';
import {
  connectionRows,
  flooringRows,
  lightingRows,
  pipeRowCells,
  pipeRows,
  preparationRows,
  scheduleStrings,
} from '../schedule';
import {
  fitViewportToBounds,
  paperSize,
  scaleLabel,
  SHEET_SCALES,
} from '../sheets';
import {
  revisionTableHeightMm,
  scheduleTableHeightMm,
  type SheetScheduleRow,
} from '../titleBlock';
import {
  deleteSelectedTitleObj,
  renderTitleObjectProps,
  resetTitleObjects,
  selectedTitleObjs,
  setTitleEdit,
  titleEditor,
} from './titleEditor';
import {
  bgImages,
  canEditStandards,
  cartiglioForSheet,
  DISCIPLINE_COLORS,
  disciplineLabel,
  DISCIPLINES,
  layerLabel,
} from '../main';
import {
  clampViewportToContent,
  fitDefaultViewport,
  modelBoundsOrNull,
  printSheet,
  schemSel,
  schemSelSet,
  selectedViewport,
  setSchemSel,
  sheetDisciplineOn,
  sheetLabel,
  sheetWhiteArea,
  viewportLocked,
} from './sheetSpace';
import { deleteSchematicSelected } from './sheetSchematic';
import {
  renderSchematicMultiProps,
  renderSchematicObjectProps,
} from './sheetObjectPanels';
import {
  addViewport,
  deleteSheet,
} from './sheetStrip';

// ---- Sheet properties panel ----
/** A sheet's Display row: `label` (English) and a Shown / Hidden toggle. The
 *  toggle is sheet data, so a click is one undo step. */
export function sheetDisplayToggle(label: string, on: boolean, set: (v: boolean) => void): HTMLElement {
  const row = document.createElement('div'); row.className = 'prop-row';
  const lab = document.createElement('label'); lab.textContent = tr(label);
  const btn = document.createElement('button');
  btn.textContent = on ? tr('Shown') : tr('Hidden');
  btn.className = on ? 'toggle active' : 'toggle';
  btn.addEventListener('click', () => { pushHistory(); set(!on); renderProperties(); invalidate(); });
  row.append(lab, btn);
  return row;
}

export function renderSheetProperties(body: HTMLElement, sh: Sheet): void {
  // A selected schematic object (box/line/connection/image) → its own
  // properties, with a link back to the sheet. Nothing selected → sheet props.
  if (schemSel && !selectedViewport()) {
    body.appendChild(buttonRow('◂ Sheet properties', () => { setSchemSel(null); renderProperties(); invalidate(); }));
    if (schemSelSet.length > 1) renderSchematicMultiProps(body, sh);
    else renderSchematicObjectProps(body, sh);
    return;
  }
  const sheetTitle = sectionTitle('Sheet');
  sheetTitle.textContent += ` — ${sheetLabel(sh)}`;
  body.appendChild(sheetTitle);

  const papers = ['A0', 'A1', 'A2', 'A3', 'A4'] as const;
  const fmtRow = document.createElement('div'); fmtRow.className = 'prop-row';
  const fmtLab = document.createElement('label'); fmtLab.textContent = tr('Paper');
  const fmtSel = document.createElement('select');
  for (const f of papers) { const o = document.createElement('option'); o.value = f; o.textContent = f; fmtSel.appendChild(o); }
  fmtSel.value = sh.paper === 'custom' ? 'A4' : sh.paper;
  fmtSel.addEventListener('change', () => { pushHistory(); sh.paper = fmtSel.value as Sheet['paper']; applyPaper(sh); });
  fmtRow.append(fmtLab, fmtSel); body.appendChild(fmtRow);

  const oriRow = document.createElement('div'); oriRow.className = 'prop-row';
  const oriLab = document.createElement('label'); oriLab.textContent = tr('Orientation');
  const oriSel = document.createElement('select');
  for (const [v, t] of [['landscape', 'Landscape'], ['portrait', 'Portrait']] as const) { const o = document.createElement('option'); o.value = v; o.textContent = t; oriSel.appendChild(o); }
  oriSel.value = sh.orientation;
  oriSel.addEventListener('change', () => { pushHistory(); sh.orientation = oriSel.value as Sheet['orientation']; applyPaper(sh); });
  oriRow.append(oriLab, oriSel); body.appendChild(oriRow);

  body.appendChild(infoRow('Size', `${Math.round(sh.widthMm)} × ${Math.round(sh.heightMm)} mm`));
  body.appendChild(buttonRow('＋ Add viewport', addViewport));
  body.appendChild(buttonRow('🖨 Print sheet (300 dpi)', () => { void printSheet(sh); }));
  body.appendChild(deleteRow(() => deleteSheet(sh.id), 'Delete sheet', true));

  // Title block: toggle direct editing (move/resize its elements on the sheet).
  // Editing the title block writes the shared cartiglio, so it's gated on the
  // `edit` permission — read-only users don't see the button.
  const tbCart = cartiglioForSheet(sh);
  if (tbCart && canEditStandards()) {
    body.appendChild(sectionTitle('Title block'));
    const tbRow = document.createElement('div'); tbRow.style.margin = '6px 0';
    const tbBtn = document.createElement('button');
    tbBtn.style.width = '100%';
    tbBtn.className = titleEditor.titleEdit ? 'primary' : '';
    tbBtn.textContent = tr(titleEditor.titleEdit ? 'Close master view' : 'Edit title block master');
    tbBtn.addEventListener('click', () => setTitleEdit(!titleEditor.titleEdit));
    tbRow.appendChild(tbBtn);
    body.appendChild(tbRow);
    if (titleEditor.titleEdit) {
      body.appendChild(buttonRow('Reset layout (this format)', () => resetTitleObjects(sh)));
      const selObjs = selectedTitleObjs(tbCart);
      if (selObjs.length === 1) renderTitleObjectProps(body, sh, tbCart, selObjs[0]);
      else if (selObjs.length > 1) {
        body.appendChild(sectionTitle(`${selObjs.length} ${tr('blocks selected')}`));
        body.appendChild(deleteRow(() => deleteSelectedTitleObj()));
        const hint = document.createElement('div'); hint.className = 'prop-empty'; hint.style.marginTop = '6px';
        hint.textContent = tr('Drag any one of them to move the whole group · copy and paste them onto another sheet too.');
        body.appendChild(hint);
      } else {
        const hint = document.createElement('div'); hint.className = 'prop-empty'; hint.style.marginTop = '6px';
        hint.textContent = tr('Drag a block from the left onto the sheet to add it. Click to select · Shift-click to multi-select · drag to move (snaps to blocks, page & 5 mm margin) · handles to resize · copy and paste work across sheets.');
        body.appendChild(hint);
      }
    }
  }

  renderRevisionsSection(body, sh);
  renderSheetDisciplinesSection(body, sh);

  const sel = selectedViewport();
  if (sel) {
    renderViewportPanel(body, sh, sel.id);
  } else {
    const hint = document.createElement('div'); hint.className = 'prop-empty';
    hint.textContent = tr('Click a viewport to select it, or add one.');
    body.appendChild(hint);
  }
}

/** A selected viewport's section of the sheet panel. */
export function renderViewportPanel(body: HTMLElement, sh: Sheet, id: string): void {
  const sel = sh.viewports.find((v) => v.id === id);
  if (!sel) return;
  body.appendChild(sectionTitle('Viewport'));
  const locked = viewportLocked(sel);
  const lockRow = document.createElement('div'); lockRow.className = 'prop-row';
  const lockLab = document.createElement('label'); lockLab.textContent = tr('Locked');
  const lockBtn = document.createElement('button');
  lockBtn.textContent = locked ? tr('Locked') : tr('Unlocked');
  lockBtn.className = locked ? 'toggle active' : 'toggle';
  lockBtn.addEventListener('click', () => { pushHistory(); sel.locked = locked ? false : undefined; renderProperties(); invalidate(); });
  lockRow.append(lockLab, lockBtn); body.appendChild(lockRow);
  const scRow = document.createElement('div'); scRow.className = 'prop-row';
  const scLab = document.createElement('label'); scLab.textContent = tr('Scale');
  const scSel = document.createElement('select');
  for (const [label, val] of SHEET_SCALES) { const o = document.createElement('option'); o.value = String(val); o.textContent = label; scSel.appendChild(o); }
  const cur = document.createElement('option'); cur.value = String(sel.scale); cur.textContent = scaleLabel(sel.scale); cur.selected = true;
  if (!SHEET_SCALES.some(([, v]) => Math.abs(v - sel.scale) < v * 0.001)) scSel.appendChild(cur);
  scSel.value = String(sel.scale);
  scSel.addEventListener('change', () => { pushHistory(); sel.scale = parseFloat(scSel.value); invalidate(); });
  scRow.append(scLab, scSel); body.appendChild(scRow);
  body.appendChild(buttonRow('Fit model to viewport', () => { const b = modelBoundsOrNull(); if (b) { pushHistory(); fitViewportToBounds(sel, b); clampViewportToContent(sel, sh); renderProperties(); invalidate(); } }));
  body.appendChild(numberRow('Width (mm)', Math.round(sel.w), (v) => { pushHistory(); sel.w = Math.max(20, v); clampViewportToContent(sel, sh); renderProperties(); invalidate(); }, { commit: true }));
  body.appendChild(numberRow('Height (mm)', Math.round(sel.h), (v) => { pushHistory(); sel.h = Math.max(20, v); clampViewportToContent(sel, sh); renderProperties(); invalidate(); }, { commit: true }));
  if (!locked) body.appendChild(deleteRow(() => deleteSchematicSelected(sh)));
  const hint = document.createElement('div'); hint.className = 'prop-empty'; hint.style.marginTop = '8px';
  hint.textContent = locked
    ? tr('Locked: unlock to move, resize or delete · double-click to pan/zoom the model inside.')
    : tr('Drag to move · handles to resize · double-click to pan/zoom the model inside.');
  body.appendChild(hint);
}

/** Default placement for a sheet's changes table: top-right corner of the
 *  content area, clear of the title block. Only set once — later moves/
 *  resizes are preserved. */
function ensureRevisionTableRect(sh: Sheet): void {
  if (sh.revisionTableRect) return;
  const area = sheetWhiteArea(sh);
  const w = Math.min(120, area.x1 - area.x0);
  sh.revisionTableRect = { x: area.x1 - w, y: area.y0, w, h: revisionTableHeightMm(w, state.drawing.revisions || [], curLang()) };
}

const CONN_COL_FRACS = [0.10, 0.22, 0.22, 0.10, 0.12, 0.24]; // No. | From | To | Length | Installed by | Description
const PREP_COL_FRACS = [0.16, 0.46, 0.38];       // No. | Part | Description
const FLOOR_COL_FRACS = [0.18, 0.40, 0.18, 0.24]; // swatch | Type | Area | Drain
const LIGHT_COL_FRACS = [0.09, 0.11, 0.38, 0.14, 0.14, 0.14]; // Tag | Symbol | Light | Rooms | Qty | Power
const PIPE_COL_FRACS = [0.06, 0.46, 0.3, 0.18]; // swatch | Pipe | Item | Qty
/** A light's tag in a schedule: the light's amber, deep enough to read on white. */
const TAG_INK = '#a86b00';
const SCHED_TABLE_W_MM = 100; // default schedule table width (10 cm)

/** A schedule table placed on a sheet: its stored rect plus the content + layout
 *  needed to draw and re-fit it. `key` selects which of the three schedules. */
interface SheetSchedTable { key: 'conn' | 'prep' | 'floor' | 'light' | 'pipe'; rect: { x: number; y: number; w: number; h: number }; title: string; headers: string[]; fracs: number[]; rows: SheetScheduleRow[]; empty: string }

/** The connection + preparation + flooring schedule rows for a sheet, filtered to
 *  the sheet's enabled disciplines and coloured by discipline. A flooring row has
 *  no code — it carries `swatchKind` so the No. cell draws the type's hatch. */
function sheetScheduleRows(sh: Sheet): { conn: SheetScheduleRow[]; prep: SheetScheduleRow[]; floor: SheetScheduleRow[]; light: SheetScheduleRow[]; pipe: SheetScheduleRow[] } {
  const conn = connectionRows(state.drawing)
    .filter((r) => sheetDisciplineOn(sh, r.type))
    .map((r) => ({ num: r.num, numColor: DISCIPLINE_COLORS[r.type], cells: [r.startName, r.endName, r.lengthText, r.installedByText, r.description] }));
  const prep = preparationRows(state.drawing)
    .filter((r) => sheetDisciplineOn(sh, r.type))
    .map((r) => ({ num: r.num, numColor: DISCIPLINE_COLORS[r.type], cells: [r.partName, r.description] }));
  const floor = sheetDisciplineOn(sh, 'flooring')
    ? flooringRows(state.drawing).map((r) => ({ num: '', numColor: DISCIPLINE_COLORS.flooring, cells: [r.typeLabel, r.area, r.drain], swatchKind: r.kind }))
    : [];
  const lightRows = sheetDisciplineOn(sh, 'lighting') ? lightingRows(state.drawing) : [];
  const light: SheetScheduleRow[] = lightRows.map((r) => ({ num: r.tag, numColor: TAG_INK, symbol: r.symbol, cells: ['', r.light, r.room, r.qty, r.power] }));
  if (light.length) light.push({ num: '', numColor: TAG_INK, cells: ['', scheduleStrings(curLang()).total, '', '', `${Math.round(lightRows.reduce((m, r) => m + r.watts, 0))} W`] });
  const pipe: SheetScheduleRow[] = pipeRows(state.drawing)
    .filter((r) => sheetDisciplineOn(sh, r.type))
    .map((r) => ({ num: '', numColor: DISCIPLINE_COLORS[r.type], cells: pipeRowCells(r) }));
  return { conn, prep, floor, light, pipe };
}

/** The schedule tables to draw on a sheet (only those with rows). */
export function sheetScheduleTables(sh: Sheet): SheetSchedTable[] {
  if (!sh.showSchedules) return [];
  ensureScheduleRects(sh); // fills any missing rect (e.g. a sheet enabled before flooring existed)
  const ss = scheduleStrings(curLang());
  const { conn, prep, floor, light, pipe } = sheetScheduleRows(sh);
  const out: SheetSchedTable[] = [];
  // Connection table: separate From / To columns for the two endpoint names.
  if (conn.length && sh.connScheduleRect) out.push({ key: 'conn', rect: sh.connScheduleRect, title: ss.connectionTitle, headers: [ss.no, ss.startPart, ss.endPart, ss.lengthCol, ss.installedByCol, ss.description], fracs: CONN_COL_FRACS, rows: conn, empty: '—' });
  if (prep.length && sh.prepScheduleRect) out.push({ key: 'prep', rect: sh.prepScheduleRect, title: ss.preparationTitle, headers: [ss.no, ss.partCol, ss.description], fracs: PREP_COL_FRACS, rows: prep, empty: '—' });
  // Flooring table: the No. column header is blank (the cell holds the hatch swatch).
  if (floor.length && sh.flooringScheduleRect) out.push({ key: 'floor', rect: sh.flooringScheduleRect, title: ss.flooringTitle, headers: ['', ss.typeCol, ss.areaCol, ss.drainCol], fracs: FLOOR_COL_FRACS, rows: floor, empty: '—' });
  if (light.length && sh.lightingScheduleRect) out.push({ key: 'light', rect: sh.lightingScheduleRect, title: ss.lightingTitle, headers: [ss.tagCol, ss.symbolCol, ss.lightCol, ss.roomCol, ss.qtyCol, ss.powerCol], fracs: LIGHT_COL_FRACS, rows: light, empty: '—' });
  if (pipe.length && sh.pipeScheduleRect) out.push({ key: 'pipe', rect: sh.pipeScheduleRect, title: ss.pipeTitle, headers: ['', ss.pipeCol, ss.itemCol, ss.qtyCol], fracs: PIPE_COL_FRACS, rows: pipe, empty: '—' });
  return out;
}

/** Default placement for a sheet's schedule tables: stacked down the right-hand
 *  content edge, below the changes table if that's shown. Set once. */
export function ensureScheduleRects(sh: Sheet): void {
  const area = sheetWhiteArea(sh);
  const w = Math.min(SCHED_TABLE_W_MM, area.x1 - area.x0);
  const { conn, prep, floor, light, pipe } = sheetScheduleRows(sh);
  let y = area.y0;
  if (sh.showRevisionTable && sh.revisionTableRect) y = sh.revisionTableRect.y + sh.revisionTableRect.h + 4;
  if (!sh.connScheduleRect) sh.connScheduleRect = { x: area.x1 - w, y, w, h: scheduleTableHeightMm(w, CONN_COL_FRACS, conn) };
  const py = sh.connScheduleRect.y + sh.connScheduleRect.h + 4;
  if (!sh.prepScheduleRect) sh.prepScheduleRect = { x: area.x1 - w, y: py, w, h: scheduleTableHeightMm(w, PREP_COL_FRACS, prep) };
  const fy = sh.prepScheduleRect.y + sh.prepScheduleRect.h + 4;
  if (!sh.flooringScheduleRect) sh.flooringScheduleRect = { x: area.x1 - w, y: fy, w, h: scheduleTableHeightMm(w, FLOOR_COL_FRACS, floor) };
  const ly = sh.flooringScheduleRect.y + sh.flooringScheduleRect.h + 4;
  if (!sh.lightingScheduleRect) sh.lightingScheduleRect = { x: area.x1 - w, y: ly, w, h: scheduleTableHeightMm(w, LIGHT_COL_FRACS, light) };
  const pw = Math.min(SCHED_TABLE_W_MM * 1.6, area.x1 - area.x0);
  if (!sh.pipeScheduleRect) sh.pipeScheduleRect = { x: area.x1 - pw, y: sh.lightingScheduleRect.y + sh.lightingScheduleRect.h + 4, w: pw, h: scheduleTableHeightMm(pw, PIPE_COL_FRACS, pipe) };
}

/** Snap-candidate edges (paper mm) of the sheet's other placed tables (changes +
 *  schedule tables), so dragging one snaps its edges to the others. */
export function otherTableEdges(sh: Sheet, exclude: { x: number; y: number; w: number; h: number }): { cx: number[]; cy: number[] } {
  const cx: number[] = [], cy: number[] = [];
  const add = (r?: { x: number; y: number; w: number; h: number }): void => {
    if (r && r !== exclude) { cx.push(r.x, r.x + r.w); cy.push(r.y, r.y + r.h); }
  };
  if (sh.showRevisionTable) add(sh.revisionTableRect);
  for (const t of sheetScheduleTables(sh)) add(t.rect);
  return { cx, cy };
}

/** Revisions section of the Sheet properties panel: the changes-table's rows
 *  (add / edit / delete, and which one new clouds are tagged with), plus a
 *  toggle to place the table on this sheet. */
function renderRevisionsSection(body: HTMLElement, sh: Sheet): void {
  body.appendChild(sectionTitle('Revisions'));
  const revs = state.drawing.revisions || [];
  const currentId = state.drawing.currentRevisionId ?? revs[revs.length - 1]?.id;
  if (!revs.length) {
    const hint = document.createElement('div'); hint.className = 'prop-empty';
    hint.textContent = tr('No revisions yet.');
    body.appendChild(hint);
  }
  for (const rev of revs) {
    const block = document.createElement('div');
    block.className = 'conn-card';
    const head = document.createElement('div');
    head.className = 'conn-head';
    const curBtn = document.createElement('button');
    curBtn.className = 'mini';
    curBtn.textContent = rev.id === currentId ? '●' : '○';
    curBtn.title = tr(rev.id === currentId ? 'Current — new clouds are tagged with this revision' : 'Set as current revision');
    curBtn.addEventListener('click', () => { pushHistory(); state.drawing.currentRevisionId = rev.id; renderProperties(); });
    const tag = document.createElement('input');
    tag.type = 'text';
    tag.value = rev.tag;
    tag.style.width = '40px';
    tag.addEventListener('change', () => { pushHistory(); rev.tag = tag.value; invalidate(); });
    const date = document.createElement('input');
    date.type = 'text';
    date.value = rev.date;
    date.style.width = '80px';
    date.addEventListener('change', () => { pushHistory(); rev.date = date.value; invalidate(); });
    const del = document.createElement('button');
    del.className = 'del mini';
    del.textContent = '✕';
    del.title = tr('Delete revision');
    del.addEventListener('click', () => {
      pushHistory();
      state.drawing.revisions = revs.filter((r) => r.id !== rev.id);
      if (state.drawing.currentRevisionId === rev.id) state.drawing.currentRevisionId = undefined;
      renderProperties();
      invalidate();
    });
    head.append(curBtn, tag, date, del);
    const fields = document.createElement('div');
    fields.className = 'conn-fields';
    const desc = document.createElement('input');
    desc.type = 'text';
    desc.placeholder = tr('What changed');
    desc.value = rev.description;
    desc.addEventListener('input', () => { rev.description = desc.value; invalidate(); });
    undoableLiveEdit(desc);
    const author = document.createElement('input');
    author.type = 'text';
    author.placeholder = tr('initials');
    author.value = rev.author || '';
    author.addEventListener('input', () => { rev.author = author.value; invalidate(); });
    undoableLiveEdit(author);
    fields.append(labeledField('Description', desc, true), labeledField('By', author));
    block.append(head, fields);
    body.appendChild(block);
  }
  body.appendChild(buttonRow('+ Add revision', () => {
    pushHistory();
    const tags = 'ABCDEFGHJKLMNPQRSTUVWXYZ'; // skip I/O — drafting convention
    const tag = tags[revs.length % tags.length] || String(revs.length + 1);
    const rev: Revision = { id: newId('rev'), tag, date: new Date().toISOString().slice(0, 10), description: '' };
    (state.drawing.revisions ??= []).push(rev);
    state.drawing.currentRevisionId = rev.id;
    renderProperties();
    invalidate();
  }));

  const tableRow = document.createElement('div'); tableRow.className = 'prop-row';
  const tableLab = document.createElement('label'); tableLab.textContent = tr('Changes table');
  const tableBtn = document.createElement('button');
  tableBtn.textContent = tr(sh.showRevisionTable ? 'Remove from sheet' : 'Add to sheet');
  tableBtn.addEventListener('click', () => {
    pushHistory();
    sh.showRevisionTable = !sh.showRevisionTable;
    if (sh.showRevisionTable) ensureRevisionTableRect(sh);
    renderProperties();
    invalidate();
  });
  tableRow.append(tableLab, tableBtn);
  body.appendChild(tableRow);
}

/** Disciplines section of the Sheet properties panel: a per-discipline show/hide
 *  toggle controlling what this sheet's viewport(s) display, plus a toggle to
 *  place the connection + preparation schedule tables on the sheet. */
function renderSheetDisciplinesSection(body: HTMLElement, sh: Sheet): void {
  if (sh.kind === 'schematic') return; // schematic sheets have no model viewport
  body.appendChild(sectionTitle('Disciplines'));
  for (const d of DISCIPLINES) {
    const on = sheetDisciplineOn(sh, d);
    const row = document.createElement('div'); row.className = 'prop-row';
    const lab = document.createElement('label');
    const dot = document.createElement('span');
    dot.style.cssText = `display:inline-block;width:10px;height:10px;border-radius:2px;margin-right:6px;vertical-align:-1px;background:${DISCIPLINE_COLORS[d]}`;
    lab.append(dot, document.createTextNode(disciplineLabel(d)));
    const btn = document.createElement('button');
    btn.textContent = tr(on ? 'Shown' : 'Hidden');
    btn.className = on ? 'toggle active' : 'toggle';
    btn.addEventListener('click', () => {
      pushHistory();
      const map = sh.disciplines ?? (sh.disciplines = {});
      map[d] = !on; // false hides; true (default) shows
      renderProperties();
      invalidate();
    });
    row.append(lab, btn);
    body.appendChild(row);
  }

  const schedRow = document.createElement('div'); schedRow.className = 'prop-row';
  const schedLab = document.createElement('label'); schedLab.textContent = tr('Schedule tables');
  const schedBtn = document.createElement('button');
  schedBtn.textContent = tr(sh.showSchedules ? 'Remove from sheet' : 'Add to sheet');
  schedBtn.addEventListener('click', () => {
    pushHistory();
    sh.showSchedules = !sh.showSchedules;
    if (sh.showSchedules) ensureScheduleRects(sh);
    renderProperties();
    invalidate();
  });
  schedRow.append(schedLab, schedBtn);
  body.appendChild(schedRow);

  // Per-sheet display toggles for hatches, product-name text and the ceilings'
  // lines, mirroring the model view's toggles (default hidden).
  body.appendChild(sectionTitle('Display'));
  body.appendChild(sheetDisplayToggle('Hatches', sh.showHatches === true, (v) => { sh.showHatches = v; }));
  body.appendChild(sheetDisplayToggle('Text', sh.showLabels === true, (v) => { sh.showLabels = v; }));
  body.appendChild(sheetDisplayToggle('Ceilings', sh.showCeilings === true, (v) => { sh.showCeilings = v; }));
  body.appendChild(sheetDisplayToggle('Beams', sh.showBeams !== false, (v) => { if (v) delete sh.showBeams; else sh.showBeams = false; }));

  // Per-sheet layers: a layer can be left out of this sheet's viewports, on top of
  // the ones hidden everywhere (those show greyed out).
  const m = layers();
  const listed = [...m.layers].reverse().filter((l) => !l.home || layerObjectIds(m, l.id).length || (l.home === 'imported' && (bgImages().length || state.drawing.background)));
  if (!listed.length) return;
  body.appendChild(sectionTitle('Layers'));
  for (const l of listed) {
    const shown = !(sh.hiddenLayers ?? []).includes(l.id);
    const row = document.createElement('div'); row.className = 'prop-row';
    const lab = document.createElement('label'); lab.textContent = layerLabel(l);
    const btn = document.createElement('button');
    btn.textContent = l.hidden || !shown ? tr('Hidden') : tr('Shown');
    btn.className = !l.hidden && shown ? 'toggle active' : 'toggle';
    if (l.hidden) { btn.disabled = true; btn.title = tr('Hidden in every view (Parts tab)'); }
    btn.addEventListener('click', () => {
      pushHistory();
      const set = new Set(sh.hiddenLayers ?? []);
      if (shown) set.add(l.id); else set.delete(l.id);
      if (set.size) sh.hiddenLayers = [...set]; else delete sh.hiddenLayers;
      renderProperties();
      invalidate();
    });
    row.append(lab, btn);
    body.appendChild(row);
  }
}

export function applyPaper(sh: Sheet): void {
  const sz = paperSize(sh);
  sh.widthMm = sz.w; sh.heightMm = sz.h;
  // Re-fit the sheet's first (default) viewport to the full white area of the
  // resized page — clear of the title blocks and inset 5 mm from every edge —
  // just like a freshly created sheet. Any extra viewports simply stay in bounds.
  if (sh.viewports[0]) fitDefaultViewport(sh.viewports[0], sh);
  for (const v of sh.viewports.slice(1)) clampViewportToContent(v, sh); // keep any extra viewports on the resized page
  redrawBackdrop();
  renderProperties();
  invalidate();
}
